import React, { ReactElement, useEffect } from 'react'
import Head from 'next/head'
import { FAVICON_LINK_KEYS } from 'shared/constants/favicon'
import { AccessDenied } from 'shared/errors/access-denied'
import { useRestrictedAccessNotification } from 'shared/hooks/use-restricted-access-notification'
import { DomainAssetsInterface } from 'shared/types/domain'
import { CommunityOwnerBlockedModal } from 'modules/community/components/community-owner-blocked-modal'
import Sidebar from 'modules/community/components/sidebar'
import JoinCommunityProvider from 'modules/community/context/join-community-provider'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import Header from 'modules/header'
import MobileNav from 'modules/header/components/mobile-nav'
import { useBrand } from 'modules/white-label/contexts/brand-context'

interface Props extends React.PropsWithChildren {
  domainAssets?: DomainAssetsInterface
}

const CommunityLayout = ({ children, domainAssets }: Props) => {
  const { data, error } = useCommunityWithTopics()
  useRestrictedAccessNotification()

  const { brand } = useBrand()

  useEffect(() => {
    if (error instanceof AccessDenied) {
      window.location.replace('/community/403')
    }
  }, [error])

  return (
    <>
      <Head>
        <title>{data?.name}</title>
        {domainAssets?.faviconUrl && (
          <link key={FAVICON_LINK_KEYS.icon} rel="icon" href={domainAssets.faviconUrl} />
        )}
      </Head>
      <Header
        logoSrc={domainAssets?.logoUrl || brand.logo?.path}
        className="w-full lg:fixed lg:top-0"
        headerContainerClassName="max-w-screen-xl mx-auto"
      />
      <MobileNav />
      <div className="lg:mt-[69px]">
        <JoinCommunityProvider>
          <div className="mx-auto flex max-w-8xl gap-9 px-0 sm:px-6 md:px-14 lg:px-[6.5rem] lg:py-9">
            <Sidebar />
            <main className="w-full min-w-0">{children}</main>
          </div>
        </JoinCommunityProvider>
      </div>
      <CommunityOwnerBlockedModal />
    </>
  )
}

export const getCommunityLayout = (
  page: ReactElement,
  props: { domainAssets?: DomainAssetsInterface },
) => <CommunityLayout domainAssets={props.domainAssets}>{page}</CommunityLayout>

export default CommunityLayout
